import { FC, useState, useEffect, useMemo } from 'react';
import { Prospect, StatusClientEnum } from 'types';
import CustomDataGrid from 'components/CustomDataGrid';
import { prospectRejectedColumns } from './Columns';
import { useQuery, WatchQueryFetchPolicy } from '@apollo/client';
import { GET_PROMOTERS_BY_STATUS } from '../queries';
import { NetworkError, NoData, RequestError } from 'components/CustomDataGrid/EmptyStates';
import { getInitialStateGrid, INITIAL_ROWS_PER_PAGE, optionsRowsPerPage } from 'utils';
import { GridPaginationModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const NO_PROSPECTS = 0;
const STEP_ID = 3;

type Props = {
  totalItems: number;
  filter: string;
  actualStep: number;
};

const Rejected: FC<Props> = ({ totalItems, actualStep, filter }) => {
  const [allProspects, setAllProspects] = useState<Prospect[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE);
  const initialStateGrid = getInitialStateGrid({ page, rowsPerPage });

  // Fetch all rejected prospects at once
  const { loading, data, error, refetch } = useQuery(GET_PROMOTERS_BY_STATUS, {
    fetchPolicy: 'no-cache',
    variables: {
      page: 0,
      limit: 10000,
      search: '',
      orderBy: 'createdAt',
      statuses: [StatusClientEnum.REJECTED, StatusClientEnum.CANCELED],
    },
  });

  useEffect(() => {
    if (!loading && data) {
      setAllProspects(data?.getClientsListByStatus || []);
    }
  }, [data, loading]);

  const filteredProspects = useMemo(() => {
    if (!filter) return allProspects;
    return allProspects.filter((p) => {
      const fullName = `${p.firstName} ${p.firstLastName} ${p.secondLastName}`.toLowerCase();
      const promoter = (p.createdBy || '').toLowerCase();
      const f = filter.toLowerCase();
      return fullName.includes(f) || promoter.includes(f);
    });
  }, [allProspects, filter]);

  const paginatedProspects = useMemo(
    () => filteredProspects.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [filteredProspects, page, rowsPerPage],
  );

  const handleChangePage = ({ page, pageSize }: GridPaginationModel) => {
    setPage(page);
    setRowsPerPage(pageSize);
  };

  useEffect(() => {
    if (actualStep === STEP_ID) {
      setPage(0);
    }
  }, [actualStep]);

  if (!navigator.onLine) return <NetworkError handleRetry={refetch} />;
  if (error) return <RequestError handleRetry={refetch} />;
  if (allProspects.length === 0 && loading === false)
    return <NoData message="No hay ningún prospecto rechazado" />;

  return (
    <CustomDataGrid
      loading={loading}
      data={paginatedProspects}
      dataColumns={prospectRejectedColumns}
      nameFilter={filter}
      title="Listado de prospectos pendientes de verificar"
      totalRows={filteredProspects.length}
      handleChangePage={handleChangePage}
      initialState={initialStateGrid}
      page={page}
      pageSize={rowsPerPage}
      pageSizeOptions={optionsRowsPerPage}
    />
  );
};

export default Rejected;
